/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, Text, Heading } from '@chakra-ui/react';

/**
 * Design-token reference (W1.4 / #679) — the primitive surface for the bidBrain theme.
 *
 * Renders the theme primitives from `src/theme.ts` live (colors, type scale, spacing,
 * radii, shadows, motion) so the token layer can be reviewed in-app. This is the
 * "Storybook (or equivalent)" surface for the token extraction. Public route:
 * `/design-tokens`.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box as="section" mb="2xl">
    <Text fontFamily="mono" fontSize="codeSm" letterSpacing="wide" textTransform="uppercase" color="fg.muted" mb="md">
      {title}
    </Text>
    {children}
  </Box>
);

const SwatchGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(150px, 1fr))" gap="md">
    {children}
  </Box>
);

const Swatch: React.FC<{ token: string; label?: string }> = ({ token, label }) => (
  <Box borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden" bg="bg.card">
    <Box h="52px" bg={token} />
    <Box p="8px">
      <Text fontSize="bodySm" fontWeight="medium" color="fg.default">
        {label ?? token}
      </Text>
      <Text fontFamily="mono" fontSize="11px" color="fg.muted">
        {token}
      </Text>
    </Box>
  </Box>
);

const BRAND_RAMP = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].map((k) => `brand.${k}`);
const HUES: [string, string][] = [
  ['signal', 'Signal Purple'],
  ['navy', 'Deep Navy'],
  ['teal', 'Adaptive Teal'],
  ['amber', 'Warm Amber'],
  ['success', 'Success'],
  ['error', 'Error'],
  ['accentGray', 'Accent Gray'],
  ['ink', 'Ink'],
];
const SEMANTIC: [string, string][] = [
  ['bg.canvas', 'Page'],
  ['bg.card', 'Card'],
  ['bg.nav', 'Nav'],
  ['fg.default', 'Text'],
  ['fg.muted', 'Text muted'],
  ['fg.title', 'Title'],
  ['border.default', 'Border'],
  ['accent.solid', 'Accent'],
  ['accent.subtle', 'Accent subtle'],
  ['success.tint', 'Success tint'],
  ['success.ink', 'Success ink'],
  ['error.tint', 'Error tint'],
  ['error.ink', 'Error ink'],
  ['warning.tint', 'Warning tint'],
  ['warning.ink', 'Warning ink'],
];
const TYPE_SCALE: [string, string][] = [
  ['display', 'Display · 60'],
  ['h1', 'Heading 1 · 44'],
  ['h2', 'Heading 2 · 32'],
  ['h3', 'Heading 3 · 22'],
  ['bodyLg', 'Body large · 18'],
  ['bodyBase', 'Body base · 16'],
  ['bodySm', 'Body small · 13'],
];
const WEIGHTS: [string, number][] = [
  ['light', 300],
  ['regular', 400],
  ['medium', 500],
  ['semibold', 600],
];
const SPACES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
const RADII = ['sm', 'md', 'lg', 'full'];
const SHADOWS = ['sm', 'md', 'lg', 'xl'];
const DURATIONS = [
  ['instant', '0ms'],
  ['fast', '100ms'],
  ['normal', '200ms'],
  ['moderate', '300ms'],
  ['slow', '500ms'],
];
const EASINGS = ['standard', 'decelerate', 'accelerate', 'sharp'];

export const DesignTokensPage: React.FC = () => (
  <Box h="100dvh" overflowY="auto" bg="bg.canvas" color="fg.default" p={{ base: 'lg', md: '2xl' }}>
    <Box maxW="layoutMax" mx="auto">
      <Text fontFamily="mono" fontSize="codeSm" letterSpacing="wide" textTransform="uppercase" color="accent.ink">
        bidBrain · W1.4
      </Text>
      <Heading as="h1" fontSize="h2" color="fg.title" mt="xs" mb="xs">
        Design Tokens
      </Heading>
      <Text color="fg.muted" mb="2xl" maxW="72ch">
        The theme primitive layer extracted from the signed-off design system (theme.ts). These are the tokens
        downstream pages, state standardization, dark mode, and whitelabel (W6) build on.
      </Text>

      <Section title="Brand ramp">
        <SwatchGrid>
          {BRAND_RAMP.map((t) => (
            <Swatch key={t} token={t} />
          ))}
        </SwatchGrid>
      </Section>

      <Section title="Brand & status hues">
        <SwatchGrid>
          {HUES.map(([t, l]) => (
            <Swatch key={t} token={t} label={l} />
          ))}
        </SwatchGrid>
      </Section>

      <Section title="Semantic tokens (theme-aware · light / dark)">
        <SwatchGrid>
          {SEMANTIC.map(([t, l]) => (
            <Swatch key={t} token={t} label={l} />
          ))}
        </SwatchGrid>
      </Section>

      <Section title="Type scale">
        <Box display="flex" flexDirection="column" gap="sm">
          {TYPE_SCALE.map(([t, l]) => (
            <Box key={t} display="flex" alignItems="baseline" gap="lg">
              <Text fontFamily="mono" fontSize="11px" color="fg.muted" w="120px" flexShrink={0}>
                {l}
              </Text>
              <Text fontSize={t} color="fg.default" lineHeight="heading" fontWeight="medium">
                Adaptive demand-path optimization
              </Text>
            </Box>
          ))}
        </Box>
      </Section>

      <Section title="Font weights">
        <Box display="flex" gap="xl" flexWrap="wrap">
          {WEIGHTS.map(([t, n]) => (
            <Text key={t} fontSize="bodyLg" fontWeight={t}>
              {t} {n}
            </Text>
          ))}
          <Text fontSize="bodyLg" fontFamily="mono" color="fg.muted">
            DM Mono · ssn-8841
          </Text>
        </Box>
      </Section>

      <Section title="Spacing">
        <Box display="flex" flexDirection="column" gap="sm">
          {SPACES.map((s) => (
            <Box key={s} display="flex" alignItems="center" gap="md">
              <Text fontFamily="mono" fontSize="11px" color="fg.muted" w="48px">
                {s}
              </Text>
              <Box h="14px" w={s} bg="accent.solid" borderRadius="sm" />
            </Box>
          ))}
        </Box>
      </Section>

      <Section title="Radii">
        <Box display="flex" gap="lg" flexWrap="wrap">
          {RADII.map((r) => (
            <Box key={r} textAlign="center">
              <Box h="64px" w="64px" bg="bg.card" borderWidth="1px" borderColor="border.default" borderRadius={r} />
              <Text fontFamily="mono" fontSize="11px" color="fg.muted" mt="xs">
                {r}
              </Text>
            </Box>
          ))}
        </Box>
      </Section>

      <Section title="Shadows">
        <Box display="flex" gap="xl" flexWrap="wrap">
          {SHADOWS.map((s) => (
            <Box key={s} textAlign="center">
              <Box h="64px" w="96px" bg="bg.card" borderRadius="lg" boxShadow={s} />
              <Text fontFamily="mono" fontSize="11px" color="fg.muted" mt="sm">
                {s}
              </Text>
            </Box>
          ))}
        </Box>
      </Section>

      <Section title="Motion">
        <Box display="flex" gap="2xl" flexWrap="wrap">
          <Box>
            <Text fontSize="bodySm" fontWeight="medium" mb="sm">
              Durations
            </Text>
            {DURATIONS.map(([t, v]) => (
              <Text key={t} fontFamily="mono" fontSize="codeSm" color="fg.muted">
                {t} · {v}
              </Text>
            ))}
          </Box>
          <Box>
            <Text fontSize="bodySm" fontWeight="medium" mb="sm">
              Easings
            </Text>
            {EASINGS.map((t) => (
              <Text key={t} fontFamily="mono" fontSize="codeSm" color="fg.muted">
                {t}
              </Text>
            ))}
          </Box>
        </Box>
      </Section>
    </Box>
  </Box>
);
